/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import anime from 'animejs';

import BigLetterN from '../icons/landing/BigLetterN';
import BigLetterT from '../icons/landing/BigLetterT';
import BigLetterE from '../icons/landing/BigLetterE';

import { getLightBackground, getDarkBackground, removeHex } from '../../lib/utils';

// import * as AppPropTypes from '../../lib/PropTypes';

import styles from '../../../styles/partials/background.scss';

const propTypes = {
    color: PropTypes.string,
    theme: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    color: null,
    theme: null,
    className: null,
};

const Background = ({ color, theme, className }) => {
    const backgroundRef = useRef();
    const innerBackgroundRef = useRef();

    const letterNRef = useRef();
    const letterTRef = useRef();
    const letterERef = useRef();

    const colorWithoutHash = removeHex(color);
    const lightBackgroundUrl = getLightBackground(colorWithoutHash);
    const darkBackgroundUrl = getDarkBackground(colorWithoutHash);

    const letterColor = theme === 'dark' ? '#FFFFFF' : color;

    useLayoutEffect(() => {
        if (theme === 'dark') {
            anime({
                targets: innerBackgroundRef.current,
                duration: 300,
                opacity: 1,
                easing: 'linear',
            });
        } else {
            anime({
                targets: innerBackgroundRef.current,
                duration: 300,
                opacity: 0,
                easing: 'linear',
            });
        }
        return () => {
            if (innerBackgroundRef.current) {
                anime.remove(innerBackgroundRef.current);
            }
        };
    }, [theme]);

    useLayoutEffect(() => {
        const duration = 30000;
        const distance = 230;
        anime.set(letterNRef.current, { translateY: -distance / 2, rotate: -10 });
        anime.set(letterTRef.current, {
            translateX: -distance,
            translateY: distance,
            rotate: 40,
        });
        anime.set(letterERef.current, {
            translateX: distance,
            translateY: distance,
            rotate: 0,
        });
        anime
            .timeline({
                duration,
                direction: 'alternate',
                easing: 'linear',
                loop: 4,
            })
            .add(
                {
                    targets: letterNRef.current,
                    translateY: -distance * 2,
                    rotate: 5,
                },
                0,
            )
            .add(
                {
                    targets: letterTRef.current,
                    translateX: -distance * 2,
                    translateY: distance * 2,
                    rotate: 20,
                },
                0,
            )
            .add(
                {
                    targets: letterERef.current,
                    translateX: distance * 2,
                    translateY: distance * 2,
                    rotate: 15,
                },
                0,
            )

            .add(
                {
                    targets: letterNRef.current,
                    translateY: -distance,
                    rotate: -10,
                },
                duration,
            )
            .add(
                {
                    targets: letterTRef.current,
                    translateX: -distance,
                    translateY: distance,
                    rotate: 40,
                },
                duration,
            )
            .add(
                {
                    targets: letterERef.current,
                    translateX: distance,
                    translateY: distance,
                    rotate: -6,
                },
                duration,
            );
        return () => {
            anime.remove([letterNRef.current, letterTRef.current, letterERef.current]);
        };
    }, []);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            ref={backgroundRef}
            style={{ backgroundImage: lightBackgroundUrl }}
        >
            <div
                className={styles.inner}
                ref={innerBackgroundRef}
                style={{ backgroundImage: darkBackgroundUrl }}
            />
            <div className={styles.letters}>
                <BigLetterN
                    ref={letterNRef}
                    color={letterColor}
                    className={classNames([styles.letter, styles.letterN])}
                />
                <BigLetterT
                    ref={letterTRef}
                    color={letterColor}
                    className={classNames([styles.letter, styles.letterT])}
                />
                <BigLetterE
                    ref={letterERef}
                    color={letterColor}
                    className={classNames([styles.letter, styles.letterE])}
                />
            </div>
        </div>
    );
};

Background.propTypes = propTypes;
Background.defaultProps = defaultProps;

export default Background;
